/* eslint-disable complexity -- source-parity lifecycle manager */
/* eslint-disable max-lines -- source-parity lifecycle manager */
/* eslint-disable no-magic-numbers -- source-parity timing values remain inline */
import type { LoggerService } from '@/services/abstract/LoggerService';
import type { HeliaLifecycleService } from '@/services/abstract/poc/HeliaLifecycleService';
import type { Libp2pService } from '@/services/abstract/poc/Libp2pService';
import type { MessagingService } from '@/services/abstract/poc/MessagingService';
import { MESH_HEARTBEAT_TOPIC, ORG_GLOBAL_TOPIC, SIX_TOPIC } from '@/config/networkConstants';
import {
  type InitializeStackResult,
  type NodeMetadata,
  P2PStackManager,
  type P2PStackInstance,
  type P2PStackOptions,
} from '@/services/abstract/poc/P2PStackManager';
import type { Logger } from '@/types/Logger';
import type {
  ConnectionInfo,
  GroupStatus,
  LibP2pNodeCallbacks,
  LibP2pWithServices,
} from '@/types/poc/libp2p';

const MESH_HEARTBEAT_INTERVAL_MS = 10_000;
const noopMessageCallback = (): void => {
  // Intentional no-op for callers that do not bridge messages into Redux.
};

const createInitialNodeMetadata = (
  sixId: string,
  bootstrapNodes: string[]
): NodeMetadata => ({
  status: {
    relayConnected: false,
    subscribedTopics: [],
    subscriberCounts: new Map(),
    connections: [],
    peerId: '',
    connectionStats: {
      activeConnections: {
        direct: 0,
        relay: 0,
        total: 0,
      },
      lifetimeStats: {
        directConnections: 0,
        relayConnections: 0,
        directDisconnections: 0,
        relayDisconnections: 0,
        totalConnections: 0,
        totalDisconnections: 0,
      },
      uniquePeers: {
        direct: new Set(),
        relay: new Set(),
        total: new Set(),
      },
    },
  },
  config: {
    groupId: sixId,
    relayAddresses: bootstrapNodes,
  },
  timers: {
    healthCheckInterval: null,
    updateInterval: null,
    reconnectInterval: null,
    topicListBroadcastInterval: null,
    sixIdAnnounceTimer: null,
  },
  reconnectState: {
    attempts: 0,
    maxAttempts: 50,
    relayPeerId: null,
  },
  peerTracking: {
    connectionTimers: new Map(),
    blockedPeers: new Set(),
    reconnectionIntervals: new Map(),
    connectionAttempts: new Map(),
    lastConnectionAttempt: new Map(),
  },
});

const createEmptyStackInstance = (sixId: string): P2PStackInstance => ({
  sixId,
  libp2pNode: null,
  heliaNode: null,
  subscriptions: null,
  isInitialized: false,
  fileListBroadcastInterval: null,
  subscriberCheckTimer: null,
  nodeMetadata: null,
  meshHeartbeatInterval: null,
});

const findStackInstance = (
  stacks: P2PStackInstance[],
  sixId: string
): P2PStackInstance | undefined => stacks.find((stack) => stack.sixId === sixId);

const removeStackInstance = (
  stacks: P2PStackInstance[],
  sixId: string
): P2PStackInstance[] => stacks.filter((stack) => stack.sixId !== sixId);

const shouldInitializeLibp2p = (options: P2PStackOptions): boolean =>
  options.autoConnectLibp2p ?? true;

const shouldInitializeHelia = (
  options: P2PStackOptions,
  stack: P2PStackInstance
): boolean => (options.autoInitializeHelia ?? true) && stack.libp2pNode !== null;

const shouldInitializeMessaging = (
  options: P2PStackOptions,
  stack: P2PStackInstance
): boolean =>
  (options.autoInitializeMessaging ?? true) &&
  stack.libp2pNode !== null &&
  stack.heliaNode !== null;

const getMessagingTopics = (sixId: string, includeHeartbeat: boolean): string[] => {
  const topics = [SIX_TOPIC(sixId), ORG_GLOBAL_TOPIC];
  if (includeHeartbeat) {
    topics.push(MESH_HEARTBEAT_TOPIC);
  }
  return topics;
};

const createSuccessResult = (
  sixId: string,
  peerId: string
): InitializeStackResult => ({
  sixId,
  peerId,
  isSuccessful: true,
});

const syncConnectionStats = (status: GroupStatus, connections: ConnectionInfo[]): void => {
  const directConnections = connections.filter((connection) => connection.isDirect).length;
  const relayConnections = connections.filter((connection) => connection.isRelay).length;

  status.connectionStats.activeConnections = {
    direct: directConnections,
    relay: relayConnections,
    total: connections.length,
  };
};

const emitStatusUpdate = (sixId: string, stack: P2PStackInstance): void => {
  if (stack.nodeMetadata === null) {
    return;
  }

  const subscriberCounts = Object.fromEntries(
    stack.nodeMetadata.status.subscriberCounts.entries()
  );
  stack.onStatusUpdate?.(sixId, subscriberCounts);
};

const emitConnectionUpdate = (
  sixId: string,
  connections: ConnectionInfo[],
  stack: P2PStackInstance
): void => {
  stack.onConnectionUpdate?.(sixId, connections);
};

export class P2PStackManagerImpl extends P2PStackManager {
  private readonly log: Logger;
  #stackInstances: P2PStackInstance[] = [];
  #heartbeatOwnerSixId: string | null = null;

  constructor(
    loggerService: LoggerService,
    private readonly libp2pService: Libp2pService,
    private readonly heliaLifecycleService: HeliaLifecycleService,
    private readonly messagingService: MessagingService
  ) {
    super();
    this.log = loggerService.createLogger({ namespace: 'six3ro:p2p:stack-manager' });
  }

  async initializeStack(options: P2PStackOptions): Promise<InitializeStackResult> {
    const { sixId } = options;
    const existingResult = this.checkExistingStack(sixId);
    if (existingResult !== null) {
      return existingResult;
    }

    this.log.info(`Initializing stack for six '${sixId}'`, options);

    const stackInstance = createEmptyStackInstance(sixId);
    stackInstance.fileListTopic = options.fileListTopic;
    stackInstance.onStatusUpdate = options.onStatusUpdate;
    stackInstance.onConnectionUpdate = options.onConnectionUpdate;
    stackInstance.nodeMetadata = createInitialNodeMetadata(sixId, options.bootstrapNodes);

    try {
      let currentStack = stackInstance;

      if (shouldInitializeLibp2p(options)) {
        currentStack = await this.initializeLibp2pLayer(currentStack, options);
      }

      if (shouldInitializeHelia(options, currentStack)) {
        currentStack = await this.initializeHeliaLayer(currentStack);
      }

      if (shouldInitializeMessaging(options, currentStack)) {
        currentStack = this.initializeMessagingLayer(currentStack, options);
      }

      currentStack.isInitialized = true;
      this.#stackInstances = [...this.#stackInstances, currentStack];

      this.log.info(`Successfully initialized stack for six '${sixId}'`);
      return createSuccessResult(sixId, currentStack.libp2pNode?.peerId.toString() ?? '');
    } catch (error) {
      await this.handleInitializationError(sixId, stackInstance, error);
      throw error;
    }
  }

  async shutdownStack(sixId: string): Promise<void> {
    const stackInstance = findStackInstance(this.#stackInstances, sixId);
    if (stackInstance === undefined) {
      this.log.warn(`No stack found for six '${sixId}'`);
      return;
    }

    await this.cleanupStackInstance(stackInstance);
    this.#stackInstances = removeStackInstance(this.#stackInstances, sixId);
  }

  async shutdownAllStacks(): Promise<void> {
    await Promise.all(
      this.#stackInstances.map(async(stackInstance) => {
        await this.cleanupStackInstance(stackInstance);
      })
    );

    this.#stackInstances = [];
  }

  async publishMessage(
    sixId: string,
    topic: string,
    data: Uint8Array | string
  ): Promise<void> {
    const stackInstance = findStackInstance(this.#stackInstances, sixId);
    if (stackInstance?.libp2pNode == null) {
      throw new Error(`No initialized LibP2P node found for six '${sixId}'`);
    }

    await this.libp2pService.publishToTopic(stackInstance.libp2pNode, topic, data);
  }

  getLibp2pNode(sixId: string): LibP2pWithServices | null {
    return findStackInstance(this.#stackInstances, sixId)?.libp2pNode ?? null;
  }

  getHeliaNode(sixId: string) {
    return findStackInstance(this.#stackInstances, sixId)?.heliaNode ?? null;
  }

  getAllSixIds(): string[] {
    return this.#stackInstances.map((stack) => stack.sixId);
  }

  hasStack(sixId: string): boolean {
    return findStackInstance(this.#stackInstances, sixId) !== undefined;
  }

  getConnectionInfo(sixId: string): ConnectionInfo[] {
    const stackInstance = findStackInstance(this.#stackInstances, sixId);
    if (stackInstance?.libp2pNode == null) {
      return [];
    }

    return stackInstance.libp2pNode.getConnections().map((connection) => {
      const address = connection.remoteAddr.toString();
      return {
        peerId: connection.remotePeer.toString(),
        address,
        isDirect: address.includes('/webrtc/') && !address.includes('/p2p-circuit'),
        isRelay:
          address.includes('/p2p-circuit') ||
          address.includes('/ws/') ||
          address.includes('/wss/'),
        direction: connection.direction,
      };
    });
  }

  reportBitswapStall(sixId: string): void {
    if (!this.hasStack(sixId)) {
      return;
    }

    this.log.warn(
      `Bitswap stall reported for six '${sixId}', but Stage 2 keeps reportBitswapStall() as a no-op`
    );
  }

  private checkExistingStack(sixId: string): InitializeStackResult | null {
    const existingStack = findStackInstance(this.#stackInstances, sixId);
    if (existingStack?.isInitialized === true) {
      this.log.info(`Stack for six '${sixId}' is already initialized`);
      return createSuccessResult(sixId, existingStack.libp2pNode?.peerId.toString() ?? '');
    }

    return null;
  }

  private async initializeLibp2pLayer(
    stack: P2PStackInstance,
    options: P2PStackOptions
  ): Promise<P2PStackInstance> {
    const callbacks = this.createNodeCallbacks(stack, options.sixId);

    stack.libp2pNode = await this.libp2pService.createNode({
      sixId: options.sixId,
      bootstrapNodes: options.bootstrapNodes,
      swarmKey: options.swarmKey,
      fileListTopic: options.fileListTopic,
      fileTransferTopic: options.fileTransferTopic,
      callbacks,
      turnConfig: options.turnConfig,
    });

    if (stack.nodeMetadata !== null) {
      stack.nodeMetadata.status.peerId = stack.libp2pNode.peerId.toString();
      this.updateConnectionsList(stack);
    }

    return stack;
  }

  private async initializeHeliaLayer(stack: P2PStackInstance): Promise<P2PStackInstance> {
    if (stack.libp2pNode == null) {
      return stack;
    }

    stack.heliaNode = await this.heliaLifecycleService.createNode(stack.libp2pNode);
    return stack;
  }

  private initializeMessagingLayer(
    stack: P2PStackInstance,
    options: P2PStackOptions
  ): P2PStackInstance {
    if (stack.libp2pNode == null || stack.heliaNode == null) {
      return stack;
    }

    const isHeartbeatOwner = this.#heartbeatOwnerSixId === null;
    if (isHeartbeatOwner) {
      this.#heartbeatOwnerSixId = options.sixId;
    }

    const topics = getMessagingTopics(options.sixId, isHeartbeatOwner);
    const subscriptionCleanup = this.messagingService.setupSubscriptions(
      stack.libp2pNode,
      stack.heliaNode,
      options.onMessage ?? noopMessageCallback,
      topics
    );

    stack.subscriptions = { cleanup: subscriptionCleanup };
    if (stack.nodeMetadata !== null) {
      stack.nodeMetadata.status.subscribedTopics = topics;
      emitStatusUpdate(options.sixId, stack);
    }

    if (isHeartbeatOwner) {
      stack.meshHeartbeatInterval = this.startMeshHeartbeat(stack, options.sixId);
    }

    return stack;
  }

  private createNodeCallbacks(
    stack: P2PStackInstance,
    sixId: string
  ): LibP2pNodeCallbacks {
    return {
      onStatusChange: (status) => {
        if (stack.nodeMetadata === null) {
          return;
        }

        stack.nodeMetadata.status = status;
        emitStatusUpdate(sixId, stack);
      },
      onConnectionOpen: (info) => {
        if (stack.nodeMetadata === null) {
          return;
        }

        const { connectionStats } = stack.nodeMetadata.status;
        connectionStats.lifetimeStats.totalConnections++;
        if (info.isDirect) {
          connectionStats.lifetimeStats.directConnections++;
          connectionStats.uniquePeers.direct.add(info.peerId);
        } else {
          connectionStats.lifetimeStats.relayConnections++;
          connectionStats.uniquePeers.relay.add(info.peerId);
        }
        connectionStats.uniquePeers.total.add(info.peerId);

        this.updateConnectionsList(stack);
      },
      onConnectionClose: (info) => {
        if (stack.nodeMetadata === null) {
          return;
        }

        const { connectionStats } = stack.nodeMetadata.status;
        connectionStats.lifetimeStats.totalDisconnections++;
        if (info.isDirect) {
          connectionStats.lifetimeStats.directDisconnections++;
        } else {
          connectionStats.lifetimeStats.relayDisconnections++;
        }

        this.updateConnectionsList(stack);
      },
      onPeerConnect: (_peerId, connections) => {
        if (stack.nodeMetadata === null) {
          return;
        }

        stack.nodeMetadata.status.connections = connections;
        syncConnectionStats(stack.nodeMetadata.status, connections);
        emitConnectionUpdate(sixId, connections, stack);
      },
      onPeerDisconnect: () => {
        this.updateConnectionsList(stack);
      },
      onSubscriptionChange: (_peerId, subscriptions) => {
        if (stack.nodeMetadata === null) {
          return;
        }

        const subscribedTopics = new Set(stack.nodeMetadata.status.subscribedTopics);
        for (const subscription of subscriptions) {
          if (subscription.subscribe) {
            subscribedTopics.add(subscription.topic);
          } else {
            subscribedTopics.delete(subscription.topic);
          }
        }

        stack.nodeMetadata.status.subscribedTopics = [...subscribedTopics];
        emitStatusUpdate(sixId, stack);
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this -- thin source-parity coordinator
  private updateConnectionsList(stack: P2PStackInstance): void {
    if (stack.libp2pNode == null || stack.nodeMetadata === null) {
      return;
    }

    const connections = stack.libp2pNode.getConnections().map((connection) => {
      const address = connection.remoteAddr.toString();
      return {
        peerId: connection.remotePeer.toString(),
        address,
        isDirect: address.includes('/webrtc/') && !address.includes('/p2p-circuit'),
        isRelay:
          address.includes('/p2p-circuit') ||
          address.includes('/ws/') ||
          address.includes('/wss/'),
        direction: connection.direction,
      };
    });

    stack.nodeMetadata.status.connections = connections;
    stack.nodeMetadata.status.relayConnected = connections.some((connection) => connection.isRelay);
    syncConnectionStats(stack.nodeMetadata.status, connections);
    emitConnectionUpdate(stack.sixId, connections, stack);
    emitStatusUpdate(stack.sixId, stack);
  }

  private async handleInitializationError(
    sixId: string,
    stackInstance: P2PStackInstance,
    error: unknown
  ): Promise<void> {
    this.log.error(`Failed to initialize stack for six '${sixId}'`, error);
    await this.cleanupStackInstance(stackInstance);
  }

  private async cleanupStackInstance(stackInstance: P2PStackInstance): Promise<void> {
    if (stackInstance.fileListBroadcastInterval !== null) {
      clearInterval(stackInstance.fileListBroadcastInterval);
      stackInstance.fileListBroadcastInterval = null;
    }

    if (stackInstance.subscriberCheckTimer !== null) {
      clearTimeout(stackInstance.subscriberCheckTimer);
      stackInstance.subscriberCheckTimer = null;
    }

    if (stackInstance.meshHeartbeatInterval !== null) {
      clearInterval(stackInstance.meshHeartbeatInterval);
      stackInstance.meshHeartbeatInterval = null;
    }

    if (stackInstance.nodeMetadata !== null) {
      const { timers, peerTracking } = stackInstance.nodeMetadata;

      if (timers.healthCheckInterval !== null) {
        clearInterval(timers.healthCheckInterval);
      }
      if (timers.updateInterval !== null) {
        clearInterval(timers.updateInterval);
      }
      if (timers.reconnectInterval !== null) {
        clearInterval(timers.reconnectInterval);
      }
      if (timers.topicListBroadcastInterval !== null) {
        clearInterval(timers.topicListBroadcastInterval);
      }
      if (timers.sixIdAnnounceTimer !== null) {
        clearTimeout(timers.sixIdAnnounceTimer);
      }

      for (const timer of peerTracking.connectionTimers.values()) {
        clearTimeout(timer);
      }
      for (const timer of peerTracking.reconnectionIntervals.values()) {
        clearInterval(timer);
      }

      peerTracking.connectionTimers.clear();
      peerTracking.reconnectionIntervals.clear();
      peerTracking.connectionAttempts.clear();
      peerTracking.lastConnectionAttempt.clear();
      peerTracking.blockedPeers.clear();
    }

    if (stackInstance.subscriptions !== null) {
      stackInstance.subscriptions.cleanup();
      stackInstance.subscriptions = null;
    }

    if (this.#heartbeatOwnerSixId === stackInstance.sixId) {
      this.#heartbeatOwnerSixId = null;
    }

    if (stackInstance.heliaNode !== null) {
      await this.heliaLifecycleService.destroyNode(stackInstance.heliaNode);
      stackInstance.heliaNode = null;
    }

    if (stackInstance.libp2pNode !== null) {
      await this.libp2pService.stopNode(stackInstance.libp2pNode);
      stackInstance.libp2pNode = null;
    }

    void this.messagingService;
    stackInstance.isInitialized = false;
  }

  private startMeshHeartbeat(
    stack: P2PStackInstance,
    sixId: string
  ): ReturnType<typeof setInterval> {
    let sequence = 0;

    const publishHeartbeat = (): void => {
      if (stack.libp2pNode == null) {
        return;
      }

      sequence += 1;
      const pubsub = stack.libp2pNode.services.pubsub;
      const heartbeat = {
        type: 'mesh-heartbeat',
        source: 'browser',
        seq: sequence,
        sixId,
        timestamp: Date.now(),
        sender: stack.libp2pNode.peerId.toString(),
        connections: stack.libp2pNode.getConnections().length,
        meshTotal: pubsub.getSubscribers(MESH_HEARTBEAT_TOPIC).length,
      };

      void this.libp2pService
        .publishToTopic(stack.libp2pNode, MESH_HEARTBEAT_TOPIC, JSON.stringify(heartbeat))
        .catch((error: unknown) => {
          this.log.error('Failed to publish mesh heartbeat', error);
        });
    };

    setTimeout(publishHeartbeat, 5_000);
    return setInterval(publishHeartbeat, MESH_HEARTBEAT_INTERVAL_MS);
  }
}
